/**
 * วิชาตัวเบา rules: what the arts learned allow (`qinggongStats`), whether one can be learned or
 * trained now, and the water a skimmer stands on. Data in data/qinggong.ts; the world calls these
 * from its movement (see world.ts: jump, dash, the qinggong run, climbing, `moveEntity`).
 * Deterministic: no randomness, no clock — everything advances by the `dt` passed in.
 */
import {
  FALL,
  FALL_ARTS,
  WATER,
  getQinggong,
  LEGACY_QINGGONG,
  QINGGONG,
  QINGGONG_BASE,
  QINGGONG_LIST,
  qinggongMax,
  type ClimbLevel,
  type DashLevel,
  type GlideLevel,
  type QinggongDef,
  type SprintLevel,
  type WaterLevel,
} from './data/qinggong';
import { QUESTS } from './data/quests';
import { deepAt, levelAt, surfaceAt, Tile, tileAt, waterSurfaceAt, type GameMap } from './map';
import type { ZoneId } from './data/zones';

/** Learned arts by id, at their levels (1 up to the art's top level). */
export type QinggongLevels = Readonly<Record<string, number>>;

/** Everything the arts learned allow, with the master art's discount already applied. */
export interface QinggongStats {
  dash: DashLevel;
  /** null: no qinggong run (Shift does nothing). */
  sprint: SprintLevel | null;
  /** Jumps before touching the ground again, the ground jump included. */
  maxJumps: number;
  airJumpStamina: number;
  /** A guard-break in mid-air gives a jump and the air dash back. */
  airReset: boolean;
  climb: ClimbLevel | null;
  water: WaterLevel | null;
  glide: GlideLevel | null;
  /** Air speed carried from a running jump (1 = none); over water when high enough. */
  leap: number;
  /** The highest fall (metres) landed without harm (`FALL.safe` by the air arts' level). */
  fallSafe: number;
}

const lv = (levels: QinggongLevels, id: string): number => {
  const q = QINGGONG[id];
  const n = levels[id] ?? 0;
  return q ? Math.max(0, Math.min(qinggongMax(q), Math.floor(n))) : 0;
};

/** The level row an art gives at `n` (1-based). */
const row = <T>(rows: readonly T[], n: number): T => rows[Math.max(0, Math.min(rows.length, n) - 1)]!;

export function qinggongStats(levels: QinggongLevels): QinggongStats {
  const s: QinggongStats = {
    dash: QINGGONG_BASE.dash,
    sprint: null,
    maxJumps: QINGGONG_BASE.jumps,
    // (A plain jump taken in the air, after stepping off a roof, costs this much.)
    airJumpStamina: 12,
    airReset: false,
    climb: null,
    water: null,
    glide: null,
    leap: 1,
    fallSafe: FALL.safe[0],
  };
  let cost = 1;
  let fallLv = 0;
  for (const q of QINGGONG_LIST) {
    const n = lv(levels, q.id);
    if (!n) continue;
    if (FALL_ARTS.includes(q.kind)) fallLv = Math.max(fallLv, q.kind === 'master' ? FALL.safe.length - 1 : n);
    switch (q.kind) {
      case 'dash':
        s.dash = row(q.levels, n);
        break;
      case 'sprint':
        s.sprint = row(q.levels, n);
        break;
      case 'skyStep': {
        const r = row(q.levels, n);
        s.maxJumps = Math.max(s.maxJumps, r.jumps);
        s.airJumpStamina = r.stamina;
        s.airReset = true;
        break;
      }
      case 'climb':
        s.climb = row(q.levels, n);
        break;
      case 'water':
        s.water = row(q.levels, n);
        break;
      case 'glide':
        s.glide = row(q.levels, n);
        break;
      case 'leap':
        s.leap = row(q.levels, n).mult;
        break;
      case 'master': {
        const r = row(q.levels, n);
        s.maxJumps += r.extraJumps;
        cost = r.cost;
        break;
      }
    }
  }
  s.fallSafe = FALL.safe[Math.min(FALL.safe.length - 1, fallLv)]!;
  if (cost !== 1) {
    const c = (v: number) => Math.round(v * cost * 10) / 10;
    s.dash = { ...s.dash, stamina: c(s.dash.stamina) };
    if (s.sprint) s.sprint = { ...s.sprint, stamina: c(s.sprint.stamina) };
    s.airJumpStamina = c(s.airJumpStamina);
    if (s.climb) s.climb = { ...s.climb, stamina: c(s.climb.stamina) };
    if (s.water) s.water = { ...s.water, stamina: c(s.water.stamina) };
    if (s.glide) s.glide = { ...s.glide, stamina: c(s.glide.stamina) };
  }
  return s;
}

/** What learning or training needs: the progress fields read (a slice of PlayerProgress). */
export interface QinggongLearner {
  readonly qinggong: QinggongLevels;
  readonly cultivation: number;
  readonly gold: number;
  readonly quest: { readonly index: number };
  /** Goods carried (pages, manuals and seals of data/qinggongDrops.ts); a test may leave it out. */
  readonly trades?: { readonly goods: Readonly<Record<string, number>> };
}

/** Why an art cannot be learned here and now (Thai, for the player), or null when it can. */
export function qinggongLearnBlock(pr: QinggongLearner, id: string, zone: ZoneId): string | null {
  const q = QINGGONG[id];
  if (!q) return 'ไม่มีวิชานี้';
  if (lv(pr.qinggong, id)) return 'เรียนวิชานี้แล้ว';
  for (const [need, n] of Object.entries(q.requires ?? {}))
    if (lv(pr.qinggong, need) < n) return `ต้องฝึก${getQinggong(need).name}ถึงขั้น ${n} ก่อน`;
  const L = q.learn;
  const byId = L.questId !== undefined ? QUESTS.findIndex((x) => x.id === L.questId) : -1;
  if (byId >= 0) {
    if (pr.quest.index <= byId) return `ต้องผ่านภารกิจ「${QUESTS[byId]!.title}」ก่อน`;
  } else if (L.questIndex !== undefined && pr.quest.index < L.questIndex)
    return `ต้องผ่านภารกิจถึง「${QUESTS[L.questIndex]?.title ?? ''}」ก่อน`;
  if (L.manual !== undefined) {
    if (!(pr.trades?.goods[L.manual ?? ''] ?? 0)) return `ต้องมีตำรา หรือรวบรวมหน้าตำราให้ครบ (${L.from})`;
  } else if (L.zones && !L.zones.includes(zone)) return `เรียนได้จาก${L.from}`;
  for (const seal of L.seals ?? []) if (!(pr.trades?.goods[seal] ?? 0)) return 'ต้องมีตราเมฆาครบสามตรา (จากบอสโลกทั้งสาม)';
  if (pr.cultivation < L.cultivation) return `แต้มฝึกปรือไม่พอ (ต้องการ ${L.cultivation})`;
  if ((L.gold ?? 0) > pr.gold) return `เงินไม่พอ (ต้องการ ${L.gold} ตำลึง)`;
  return null;
}

/** Cultivation to raise an art from its current level, or null at the top (or not learned). */
export function qinggongTrainCost(q: QinggongDef, level: number): number | null {
  if (level < 1 || level >= qinggongMax(q)) return null;
  return q.train[level - 1] ?? null;
}

/** Why an art cannot be trained now, or null when it can. */
export function qinggongTrainBlock(pr: QinggongLearner, id: string, zone?: ZoneId): string | null {
  const q = QINGGONG[id];
  if (!q) return 'ไม่มีวิชานี้';
  const n = lv(pr.qinggong, id);
  if (!n) return 'ยังไม่ได้เรียนวิชานี้';
  // A dropped art's manual opens levels up to `selfCap`; beyond that the teacher must be in reach (the zone, when known).
  const cap = q.learn.selfCap;
  if (zone && cap !== undefined && n >= cap && !q.learn.zones?.includes(zone)) return `ขั้นสูงต้องให้ครูสอน: ${q.learn.from}`;
  const cost = qinggongTrainCost(q, n);
  if (cost === null) return 'ฝึกถึงขั้นสูงสุดแล้ว';
  if (pr.cultivation < cost) return `แต้มฝึกปรือไม่พอ (ต้องการ ${cost})`;
  return null;
}

/** A save's qinggong repaired against the data: known arts, levels within range. */
export function repairQinggong(raw: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const [id, n] of Object.entries(raw as Record<string, unknown>)) {
    const q = QINGGONG[id];
    if (q && typeof n === 'number' && Number.isFinite(n) && n >= 1)
      out[id] = Math.min(qinggongMax(q), Math.floor(n));
  }
  return out;
}

/**
 * A save's qinggong: kept as saved, or — for a save from before arts were learned (no field at
 * all) — the arts that match what the character could already do, so nobody loses a way of moving.
 */
export function migrateQinggong(raw: unknown): Record<string, number> {
  return raw === undefined ? { ...LEGACY_QINGGONG } : repairQinggong(raw);
}

/** Every art at its top level (GM). */
export const allQinggong = (): Record<string, number> =>
  Object.fromEntries(QINGGONG_LIST.map((q) => [q.id, qinggongMax(q)]));

/** The sea or a river at a point (what water skimming stands on). */
export const isWaterAt = (m: GameMap, x: number, y: number): boolean =>
  tileAt(m, Math.floor(x), Math.floor(y)) === Tile.Water;

/**
 * How deep the water is at a point (metres, surface to bed; 0 off the water): the water's surface
 * over the bed's level where they differ (a region's rivers and lakes), else the sea's depth class
 * (`deep` layer, `WATER.classDepth`); water with neither is deep.
 */
export function waterDepthAt(m: GameMap, x: number, y: number): number {
  if (!isWaterAt(m, x, y)) return 0;
  const d = m.chunks ? waterSurfaceAt(m, x, y) - levelAt(m, x, y) : 0;
  if (d > 0.05) return d;
  return WATER.classDepth[deepAt(m, Math.floor(x), Math.floor(y))] ?? WATER.classDepth[0];
}

/**
 * How the player is moving, for the renderer's motions (round 2) and the rules that read it:
 * `ground` on its feet, `air` jumping, falling, gliding or wall-running (`Entity.climbing`), `skim`
 * running on water, `wade` in shallow water, `swim` at the surface of deep water, `dive` under it,
 * `climb` hand over hand up a cliff, `ledge` holding on at a ledge of one.
 */
export type MoveMode = 'ground' | 'air' | 'skim' | 'wade' | 'swim' | 'dive' | 'climb' | 'ledge';

/** In the water (not on it). */
export const inWater = (st: { mode: MoveMode }): boolean =>
  st.mode === 'wade' || st.mode === 'swim' || st.mode === 'dive';
/** Swimming or diving: no weapons, no skills, no jumps. */
export const swimming = (st: { mode: MoveMode }): boolean => st.mode === 'swim' || st.mode === 'dive';
/** On a cliff face. */
export const onCliff = (st: { mode: MoveMode }): boolean => st.mode === 'climb' || st.mode === 'ledge';

/** The player's qinggong state between ticks, read by the client for the animation. */
export interface QinggongState {
  /** Skimming across water this tick. */
  onWater: boolean;
  /** Gliding down (ร่อนลม) until the ground or a press of jump. */
  gliding: boolean;
  /** Air speed carried from a running jump (1 = none); set on take-off, cleared on landing. */
  leap: number;
  /** The last dry ground stood on. */
  dryX: number;
  dryY: number;
  /**
   * The height a fall counts from (metres above the sea): the top of the jump, or where a glide, a
   * wall-run or an updraft last let go (`trackFall`). NaN on the ground.
   */
  fallTop: number;
  /** How the player is moving (`MoveMode`). */
  mode: MoveMode;
  /** The water's depth underfoot (metres; 0 out of it). */
  depth: number;
  /** Metres under the surface while diving (0 at the surface). */
  dive: number;
  /** Seconds of breath (ลมหายใจ) left, up to `WATER.breath`. */
  breath: number;
  /** The wave's height at the swimmer (metres over the still surface), and the push of the water (metres a second). */
  wave: number;
  currentX: number;
  currentY: number;
  /** On a cliff: the way into the face (a unit vector), and the ground level (world pixels) the climb began from. */
  climbDX: number;
  climbDY: number;
  climbFoot: number;
}

export const newQinggongState = (x: number, y: number): QinggongState => ({
  onWater: false,
  gliding: false,
  leap: 1,
  dryX: x,
  dryY: y,
  fallTop: Number.NaN,
  mode: 'ground',
  depth: 0,
  dive: 0,
  breath: WATER.breath,
  wave: 0,
  currentX: 0,
  currentY: 0,
  climbDX: 0,
  climbDY: 0,
  climbFoot: 0,
});

/** Whether stamina comes back: on the feet, out of deep water, not skimming or on a cliff. */
export const restsStamina = (st: QinggongState): boolean => !st.onWater && !swimming(st) && !onCliff(st);
/** The walking speed multiplied in the water (1 out of it). */
export const waterSpeed = (st: QinggongState): number =>
  st.mode === 'wade' ? WATER.speed.wade : st.mode === 'swim' ? WATER.speed.swim : st.mode === 'dive' ? WATER.speed.dive : 1;

/**
 * One tick of the arts that run on their own: skimming water (stamina, or dropping into it),
 * gliding (stamina, or folding the arms), and remembering dry ground; it also sets the mode
 * (ground, air, skim, wade, swim; systems/water.ts adds diving, systems/climb.ts the cliff). Returns
 * 'sink' when the player went into the water from a skim or from the air (a splash, then swimming
 * or wading where it happened), 'glideEnd' when a glide ran out of stamina.
 */
export function tickQinggong(
  m: GameMap,
  p: { x: number; y: number; z: number; stamina: number },
  s: QinggongStats,
  st: QinggongState,
  sealed: boolean,
  dt: number,
): 'sink' | 'glideEnd' | null {
  const grounded = p.z <= 0;
  const wet = grounded && isWaterAt(m, p.x, p.y);
  const wasOn = st.onWater;
  const wasIn = inWater(st);
  const fromAir = st.mode === 'air';
  st.onWater = false;
  st.depth = wet ? waterDepthAt(m, p.x, p.y) : 0;
  if (grounded) {
    st.gliding = false;
    st.leap = 1;
    st.fallTop = Number.NaN;
    if (!wet && surfaceAt(m, p.x, p.y) !== null) [st.dryX, st.dryY] = [p.x, p.y];
  }
  if (wet) {
    // วิ่งบนน้ำ: kept up while it lasts; taken up stepping in with enough stamina or coming down from the air.
    const sw = s.water;
    if (sw && !sealed && p.stamina > 0 && (wasOn || (!wasIn && (fromAir || p.stamina >= sw.enter)))) {
      p.stamina = Math.max(0, p.stamina - sw.stamina * dt);
      if (p.stamina > 0) {
        st.onWater = true;
        st.mode = 'skim';
        return null;
      }
    }
    // In the water: waded where shallow, swum where deep (a diver stays under: systems/water.ts).
    if (st.depth < WATER.swimDepth) st.mode = 'wade';
    else if (st.mode !== 'dive') st.mode = 'swim';
    return wasOn || (!wasIn && fromAir && st.mode !== 'wade') ? 'sink' : null;
  }
  if (grounded) st.mode = 'ground';
  else if (!onCliff(st)) st.mode = 'air';
  st.dive = 0;
  if (st.gliding) {
    // Drifting is free at stamina 0 a second (FALL's note in data/qinggong.ts): it never runs out.
    if (!s.glide || sealed || (s.glide.stamina > 0 && p.stamina <= 0)) {
      st.gliding = false;
      return 'glideEnd';
    }
    p.stamina = Math.max(0, p.stamina - s.glide.stamina * dt);
  }
  return null;
}

/**
 * Keeps the height a fall counts from, each tick in the air (`alt`: metres above the sea). Going up
 * (a jump, a wall-run, an updraft) or held on a flying mount (`held`: the ground under it, so
 * losing the mount is no fall) starts it again; an open glide brings it down toward the glider at
 * `FALL.glideBrake` metres a second; a free fall keeps it.
 */
export function trackFall(st: QinggongState, alt: number, rising: boolean, held: number | null, dt: number): void {
  if (held !== null) st.fallTop = held;
  else if (rising || Number.isNaN(st.fallTop)) st.fallTop = alt;
  else if (st.gliding) st.fallTop = Math.min(st.fallTop, Math.max(alt, st.fallTop - FALL.glideBrake * dt));
}

/** The share of the health bar a fall of `h` metres takes (0 up to the safe height; 1 or more is death from full). */
export function fallDamage(h: number, safe: number): number {
  if (!(h > safe)) return 0;
  return ((h - safe) / ((FALL.lethal - 1) * safe)) ** FALL.power;
}

/** The metres a landing at `alt` fell (0 into water, or with no fall under way). */
export function fallHeight(st: QinggongState, alt: number, wet: boolean): number {
  return wet || Number.isNaN(st.fallTop) ? 0 : Math.max(0, st.fallTop - alt);
}
